import React, { Component, Fragment } from "react";
import { fromJS, is } from "immutable";
import { withTranslation } from "react-i18next";
import "component/filters/CategoryFilters/styles.scss";
import { ReactComponent as FilterIcon } from "assets/icons/filter.svg";
import ScrollDirectionTracker from "utils/ScrollDirectionTracker.js";

// The Types tab is addressed by its Elasticsearch field, so its rel segment is
// "game-models" while the label it renders is the "types" i18n key. The two
// deliberately disagree — see PLAY-3326 backend design D11.
const isTypesRel = (rel) => rel.split(".").pop() === "game-models";

class CategoryFilters extends Component {
  constructor(props) {
    super(props);
    this.state = { isNavHidden: false, isRefreshingStale: false };

    this.scrollTracker = new ScrollDirectionTracker({
      scrollContainerSelector: ".main-content",
      threshold: 8,
      topOffset: 20,
      onChange: (isHidden) => this.setState({ isNavHidden: isHidden }),
    });
  }

  componentDidMount() {
    this._syncAutoHide();

    if (
      this.props.filterRoute &&
      this.props.fetchFilters &&
      (!this.props.filters || this._hasStaleFilters())
    ) {
      if (this.props.filters) {
        this.setState({ isRefreshingStale: true });
      }

      this.props.fetchFilters(this.props.filterRoute);
    }
  }

  _hasStaleFilters() {
    return (
      !!this.props.filterRoute &&
      this.props.filtersCachedHref !== undefined &&
      this.props.filtersCachedHref !== this.props.filterRoute.get("href")
    );
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (
      this.props.filterRoute &&
      !is(this.props.filterRoute, prevProps.filterRoute) &&
      this.props.fetchFilters
    ) {
      this.props.fetchFilters(this.props.filterRoute);
    }

    if (
      this.props.filters &&
      !is(this.props.filters, prevProps.filters) &&
      this.props.filtersIsLoading
    ) {
      this.props.fetchFilters(this.props.filterRoute);
    }

    if (
      this.state.isRefreshingStale &&
      ((prevProps.filtersIsLoading && !this.props.filtersIsLoading) ||
        !is(this.props.filters, prevProps.filters))
    ) {
      this.setState({ isRefreshingStale: false });
    }

    this._syncAutoHide();
  }

  componentWillUnmount() {
    this.scrollTracker.stop();
  }

  _syncAutoHide() {
    if (this.props.isMobile && !this.props.disableAutoHide) {
      this.scrollTracker.start();
    } else {
      this.scrollTracker.stop();
    }
  }

  renderSortTitle(sortBy) {
    const { t } = this.props;
    let sortTitle = "";

    switch (sortBy) {
      case "editor":
        sortTitle = t("sortEditor");
        break;
      case "popularity":
        sortTitle = t("sortPopularity");
        break;
      case "newest":
        sortTitle = t("sortNewest");
        break;
      case "name":
        sortTitle = t("sortName");
        break;
      case "lastPlayed":
        sortTitle = t("sortLastPlayed");
        break;
      case "relevance":
        sortTitle = t("sortedRelevance");
        break;
      default:
        sortTitle = "Default";
        break;
    }

    const safeTitle = sortTitle || "";

    if (safeTitle.includes("並べ替え") || safeTitle.includes("排序")) {
      return (
        <span className="sorted-by">
          &#8226;
          <span
            className="sort-btn"
            onClick={(event) => {
              event.stopPropagation();
              this.props.onSortClick();
            }}
          >
            {safeTitle}
          </span>
        </span>
      );
    } else {
      return (
        <span data-testid="sort-by-value" className="sorted-by">
          &#8226; {t("sortedBy")}
          <span
            className="sort-btn"
            onClick={(event) => {
              event.stopPropagation();
              this.props.onSortClick();
            }}
          >
            {safeTitle.split("by").pop().trim()}
          </span>
        </span>
      );
    }
  }

  getLabel(rel) {
    switch (true) {
      case rel.includes("providers"):
        return this.props.t("providers");
      case rel.includes("themes"):
        return this.props.t("themes");
      case rel.includes("features"):
        return this.props.t("features");
      // rel segment "game-models" -> "types" label: intentional, not a typo.
      case isTypesRel(rel):
        return this.props.t("types");
      default:
        return this.props.t("games");
    }
  }

  render() {
    const { t } = this.props;
    const { isNavHidden, isRefreshingStale } = this.state;
    const filters = isRefreshingStale ? null : this.props.filters;

    if (!this.props.filterRoute) {
      return null;
    }

    return (
      <div
        className={`category-filters-container ${
          isNavHidden ? "nav-hidden" : ""
        }`}
      >
        {this.props.filterRoute && !filters && (
          <div className="category-filters">
            <span className="badge badge-secondary">
              <span className="dummy">XX XXXX</span>
            </span>
            {fromJS([1, 2, 3, 4]).map((filter, key) => {
              return (
                <span className="badge badge-secondary" key={key}>
                  <span className="dummy">XXXX XXXXXX</span>
                </span>
              );
            })}
          </div>
        )}
        {filters && (
          <div className="category-filters-toggled">
            {this.props.link.get("rel").indexOf("providers") === -1 &&
              this.props.link.get("rel").indexOf("special-features") === -1 &&
              this.props.link.get("rel").indexOf("themes") === -1 &&
              !isTypesRel(this.props.link.get("rel")) && (
                <span
                  className={`sorting-filters ${
                    this.props.sortingSize >= 1 ? "active" : ""
                  }`}
                  onClick={(event) => {
                    event.stopPropagation();
                    this.props.onSortClick();
                  }}
                >
                  {filters.filter(
                    (filter) => filter.get("values").size !== 1,
                  ).size !== 0 && (
                    <Fragment>
                      <FilterIcon />
                      {!this.props.isMobile ? <span>{t("filter")}</span> : ""}
                    </Fragment>
                  )}
                </span>
              )}

            {this.props.navigationMainFilters
              ? this.props.navigationMainFilters.map((filterId, key) => {
                  const filter = filters.find(
                    (f) => f.get("id") === filterId,
                  );

                  if (!filter) return null;

                  if (
                    filter.get("values").size === 1 ||
                    filter.get("visible") === false
                  ) {
                    return null;
                  }

                  return (
                    <span
                      className="badge badge-secondary"
                      key={key}
                      onClick={(event) => {
                        event.stopPropagation();
                        this.props.onApplyFilterClickHandler(filter);
                      }}
                    >
                      {filter.get("name")}
                    </span>
                  );
                })
              : filters.map((filter, key) => {
                  if (
                    filter.get("values").size === 1 ||
                    filter.get("visible") === false ||
                    filter.get("isExpandable")
                  ) {
                    return null;
                  }

                  return (
                    <span
                      className="badge badge-secondary"
                      key={key}
                      onClick={(event) => {
                        event.stopPropagation();
                        this.props.onApplyFilterClickHandler(filter);
                      }}
                    >
                      {filter.get("name")}
                    </span>
                  );
                })}
          </div>
        )}
        {!filters && (
          <div className="category-filters-toggled">
            <span className="sorting-filters">
              <FilterIcon />
              {!this.props.isMobile ? <span>{t("filter")}</span> : ""}
              {fromJS([1, 2, 3, 4, 5, 6]).map((filter, key) => {
                return (
                  <span className="badge badge-secondary" key={key}>
                    <span className="dummy">XXXXXXX</span>
                  </span>
                );
              })}
            </span>
          </div>
        )}
        {this.props.link &&
        this.props.link.get("rel").indexOf("providers") === -1 &&
        this.props.link.get("rel").indexOf("special-features") === -1 &&
        this.props.link.get("rel").indexOf("themes") === -1 &&
        !isTypesRel(this.props.link.get("rel"))
          ? filters && (
              <div className="sub-category-filters-toggled">
                {filters.map((filter, key) => {
                  return (
                    <Fragment key={key}>
                      {filter.get("values") &&
                        filter.get("values").map((value, index) => {
                          if (
                            !value.get("toggled") ||
                            !value.get("enabled") ||
                            filter.get("isExpandable")
                          ) {
                            return null;
                          }

                          return (
                            <span
                              className="badge badge-toggled"
                              key={index}
                              onClick={(event) => {
                                event.stopPropagation();
                                this.props.onFilterClickHandler(
                                  this.props.filterRoute,
                                  filter.get("id"),
                                  value,
                                );
                              }}
                            >
                              {value.get("name")}
                            </span>
                          );
                        })}
                    </Fragment>
                  );
                })}
                {filters.map((filter) => {
                  if (
                    filter.get("values").size === 1 ||
                    !filter.get("isExpandable") ||
                    filter.get("visible") === false
                  ) {
                    return null;
                  }

                  return filter.get("values").map((value, key) => {
                    if (value.get("count") === 0) {
                      return null;
                    }

                    return value.get("toggled") ? (
                      <span
                        className={`badge badge-toggled ${
                          filter.get("isMultiSelectable") ? "category" : "type"
                        }`}
                        key={key}
                        onClick={(event) => {
                          event.stopPropagation();
                          this.props.onFilterClickHandler(
                            this.props.filterRoute,
                            filter.get("id"),
                            value,
                          );
                        }}
                      >
                        {value.get("name")}
                      </span>
                    ) : (
                      <span
                        className={`badge badge-secondary ${
                          filter.get("isMultiSelectable") ? "category" : "type"
                        }`}
                        key={key}
                        onClick={(event) => {
                          event.stopPropagation();
                          this.props.onFilterClickHandler(
                            this.props.filterRoute,
                            filter.get("id"),
                            value,
                          );
                        }}
                      >
                        {value.get("name")}
                      </span>
                    );
                  });
                })}
              </div>
            )
          : filters && (
              <div className="sub-category-filters-toggled providers">
                <span
                  data-testid="filter-button"
                  className={`sorting-filters ${
                    this.props.sortingSize >= 1 ? "active" : ""
                  }`}
                  onClick={(event) => {
                    event.stopPropagation();
                    this.props.onSortClick();
                  }}
                >
                  <FilterIcon />
                  {!this.props.isMobile ? <span>{t("filter")}</span> : ""}
                </span>
                {filters.map((filter) => {
                  if (
                    filter.get("values").size === 1 ||
                    !filter.get("isExpandable") ||
                    filter.get("visible") === false
                  ) {
                    return null;
                  }

                  return filter.get("values").map((value, key) => {
                    return (
                      <span
                        className={`badge ${
                          value.get("toggled")
                            ? "badge-toggled"
                            : "badge-secondary"
                        } `}
                        key={key}
                        onClick={(event) => {
                          event.stopPropagation();
                          this.props.onFilterClickHandler(
                            this.props.filterRoute,
                            filter.get("id"),
                            value,
                          );
                        }}
                      >
                        {value.get("name")}
                      </span>
                    );
                  });
                })}
              </div>
            )}
        {this.props.link &&
          this.props.games &&
          this.props.games.getIn(["items"]) && (
            <div className="row nopadding">
              <span className="col-auto nopadding">
                <span data-testid="results-count" className="total-game-count">
                  {this.props.games.getIn(["totalCount"])}{" "}
                  {this.getLabel(this.props.link.get("rel"))}
                  {this.props.sortBy
                    ? this.renderSortTitle(this.props.sortBy)
                    : ""}
                </span>
              </span>
              {this.props.enableClearAll && (
                <span className="col">
                  {" "}
                  <u
                    className="clear-all"
                    onClick={(event) => {
                      event.stopPropagation();
                      this.props.clearAllFilters(
                        this.props.filterRoute.get("rel"),
                      );
                    }}
                  >
                    {t("clearAllFilters")}
                  </u>
                </span>
              )}
            </div>
          )}
        {!this.props.games && (
          <div className="row nopadding">
            <span className={"col nopadding"}>
              <span className="total-game-count">
                <span className="dummy">XXXXXXX</span> •{" "}
                <span className="dummy">XXXXXXXXXXXX </span>
                <span className="dummy">XXXXXXX</span>
              </span>
            </span>
          </div>
        )}
      </div>
    );
  }
}

export default withTranslation("translation")(CategoryFilters);
